import React, { useState, useEffect, useRef } from 'react';
import Style from './Home.module.scss';
import me from '../../img/self.png';
import classNames from 'classnames';
import EmojiBullet from "./EmojiBullet";
import SocialIcon from "./SocialIcon";
import { Box } from "@mui/material";
import { info } from "../../info/Info";

/* ─────────────────────────────────────────
   DRAFTING GRID — ultra-subtle SVG background
───────────────────────────────────────── */
function DraftingGrid() {
  return (
    <div className={Style.draftingGrid} aria-hidden="true">
      <svg className={Style.draftingGridSvg} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="smallGrid" width="28" height="28" patternUnits="userSpaceOnUse">
            <path d="M 28 0 L 0 0 0 28" fill="none" stroke="currentColor" strokeWidth="0.4" />
          </pattern>
          <pattern id="largeGrid" width="140" height="140" patternUnits="userSpaceOnUse">
            <rect width="140" height="140" fill="url(#smallGrid)" />
            <path d="M 140 0 L 0 0 0 140" fill="none" stroke="currentColor" strokeWidth="0.9" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#largeGrid)" />
      </svg>
    </div>
  );
}

/* ─────────────────────────────────────────
   AMBIENT BLOBS — give the glass something
   to refract against
───────────────────────────────────────── */
function AmbientBlobs() {
  return (
    <>
      <div className={Style.blobA} aria-hidden="true" />
      <div className={Style.blobB} aria-hidden="true" />
    </>
  );
}

/* ─────────────────────────────────────────
   AVATAR FRAME — precision instrument style
───────────────────────────────────────── */
function AvatarFrame({ gradient, src }) {
  const svgRef = useRef(null);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const ns = 'http://www.w3.org/2000/svg';

    // Tick marks at cardinal + 45° positions
    [0, 45, 90, 135, 180, 225, 270, 315].forEach((deg, i) => {
      const rad = (deg * Math.PI) / 180;
      const cx = 156, cy = 156, r = 138;
      const len = i % 2 === 0 ? 11 : 5.5;
      const x1 = cx + (r - 1)   * Math.cos(rad);
      const y1 = cy + (r - 1)   * Math.sin(rad);
      const x2 = cx + (r - len) * Math.cos(rad);
      const y2 = cy + (r - len) * Math.sin(rad);
      const l = document.createElementNS(ns, 'line');
      l.setAttribute('x1', x1.toFixed(2)); l.setAttribute('y1', y1.toFixed(2));
      l.setAttribute('x2', x2.toFixed(2)); l.setAttribute('y2', y2.toFixed(2));
      l.setAttribute('stroke', '#1a3050');
      l.setAttribute('stroke-width',   i % 2 === 0 ? '1.5' : '0.8');
      l.setAttribute('stroke-opacity', i % 2 === 0 ? '0.55' : '0.28');
      svg.appendChild(l);
    });

    // Corner crosshair accents
    [
      [14,14,28,14],[14,14,14,28],[298,14,284,14],[298,14,298,28],
      [14,298,28,298],[14,298,14,284],[298,298,284,298],[298,298,298,284],
    ].forEach(([x1,y1,x2,y2]) => {
      const l = document.createElementNS(ns, 'line');
      l.setAttribute('x1', x1); l.setAttribute('y1', y1);
      l.setAttribute('x2', x2); l.setAttribute('y2', y2);
      l.setAttribute('stroke', '#1a3050');
      l.setAttribute('stroke-width', '1');
      l.setAttribute('stroke-opacity', '0.35');
      svg.appendChild(l);
    });

    // Cleanup on unmount / re-mount (StrictMode double-invoke safety)
    return () => {
      while (svg.firstChild) svg.removeChild(svg.firstChild);
    };
  }, []);

  return (
    <div className={Style.avatarWrap}>
      {/* Precision decoration */}
      <svg
        ref={svgRef}
        className={Style.avatarDecor}
        viewBox="0 0 312 312"
        fill="none"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"   stopColor="#1a3050" stopOpacity="0.65" />
            <stop offset="100%" stopColor="#457b9d" stopOpacity="0.18" />
          </linearGradient>
        </defs>
        {/* Solid arcs */}
        <path d="M 156 18 A 138 138 0 0 1 294 156" stroke="url(#arcGrad)" strokeWidth="1" strokeLinecap="round" />
        <path d="M 156 294 A 138 138 0 0 1 18 156"  stroke="url(#arcGrad)" strokeWidth="1" strokeLinecap="round" />
        {/* Dashed outer arcs */}
        <path d="M 156 8 A 148 148 0 0 1 304 156"   stroke="rgba(69,123,157,0.15)" strokeWidth="0.5" strokeLinecap="round" strokeDasharray="4 8" />
        <path d="M 156 304 A 148 148 0 0 1 8 156"   stroke="rgba(69,123,157,0.15)" strokeWidth="0.5" strokeLinecap="round" strokeDasharray="4 8" />
      </svg>

      {/* Photo */}
      <Box
        component="img"
        src={src}
        alt="Madhu Vanguri"
        className={Style.avatar}
        style={{ background: gradient }}
        fetchPriority="high"
      />

      {/* Role badge */}
      <div className={Style.roleBadge}>
        <span className={Style.roleBadgeDot} aria-hidden="true" />
        <span>Mechanical Design Engineer</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   TYPEWRITER — fixed cycling logic
   Bug fix (kept from prior version): original
   set paused+deleting in the same tick, skipping
   the hold delay. Uses a dedicated `pausing` flag
   so the full word displays, holds 2 s, then
   deletes cleanly.

   Accessibility: the animated span is hidden from
   assistive tech (purely decorative motion); a
   static, fully-formed label communicates the same
   content once.
───────────────────────────────────────── */
function Typewriter({ texts }) {
  const [display,  setDisplay]  = useState('');
  const [roleIdx,  setRoleIdx]  = useState(0);
  const [charIdx,  setCharIdx]  = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [pausing,  setPausing]  = useState(false);

  const list = Array.isArray(texts) ? texts : [texts];
  const staticLabel = list.join(' · ');

  useEffect(() => {
    // While pausing: wait 2 s, then switch to deleting
    if (pausing) {
      const t = setTimeout(() => {
        setPausing(false);
        setDeleting(true);
      }, 2000);
      return () => clearTimeout(t);
    }

    const current = list[roleIdx % list.length];
    const speed   = deleting ? 26 : 62;

    const t = setTimeout(() => {
      if (!deleting) {
        if (charIdx < current.length) {
          setDisplay(current.slice(0, charIdx + 1));
          setCharIdx(c => c + 1);
        } else {
          // Finished typing — enter pause before deleting
          setPausing(true);
        }
      } else {
        if (charIdx > 0) {
          setDisplay(current.slice(0, charIdx - 1));
          setCharIdx(c => c - 1);
        } else {
          // Finished deleting — move to next role
          setDeleting(false);
          setRoleIdx(r => r + 1);
        }
      }
    }, speed);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [charIdx, deleting, pausing, roleIdx]);

  return (
    <div className={Style.typewriter} aria-label={staticLabel}>
      <span className={Style.typewriterPrompt} aria-hidden="true">_</span>
      <span className={Style.typewriterText} aria-hidden="true">{display}</span>
      <span className={Style.typewriterCursor} aria-hidden="true">|</span>
    </div>
  );
}

/* ─────────────────────────────────────────
   SPEC TAG — inline technical label chips
───────────────────────────────────────── */
function SpecTag({ label }) {
  return <span className={Style.specTag}>{label}</span>;
}

/* ─────────────────────────────────────────
   DOWNLOAD BUTTON — refined CTA with shine
───────────────────────────────────────── */
function DownloadButton() {
  return (
    <a href="/Madhu_Vanguri_Resume.pdf" download className={Style.downloadBtn}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round"
           aria-hidden="true">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="7,10 12,15 17,10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
      <span>Download Résumé</span>
    </a>
  );
}

/* ─────────────────────────────────────────
   HOME — MAIN EXPORT
───────────────────────────────────────── */
export default function Home({ innerRef }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60);
    return () => clearTimeout(t);
  }, []);

  const typewriterTexts = Array.isArray(info.position) ? info.position : [info.position];
  const specs = ['SolidWorks', 'GD&T', 'ANSYS FEA', 'DFM / DFA', 'Medical Imaging'];

  return (
    <Box
      ref={innerRef}
      component="main"
      className={classNames(Style.home, { [Style.mounted]: mounted })}
      id="home"
      display="flex"
      flexDirection={{ xs: 'column', md: 'row' }}
      alignItems="center"
      justifyContent="center"
      minHeight="calc(100vh - 175px)"
    >
      {/* Backgrounds */}
      <DraftingGrid />
      <AmbientBlobs />

      {/* ── LEFT: Avatar ── */}
      <Box
        className={Style.avatarSection}
        mb={{ xs: '3rem', md: 0 }}
        mr={{ xs: 0, md: '5rem' }}
      >
        <AvatarFrame gradient={info.gradient} src={me} />
      </Box>

      {/* ── RIGHT: Glass card ── */}
      <div className={Style.card}>

        {/* Section label */}
        <div className={Style.sectionLabel}>
          <span className={Style.labelLine} aria-hidden="true" />
          <span>Portfolio · 2026</span>
          <span className={Style.labelLine} aria-hidden="true" />
        </div>

        {/* Name */}
        <h1 className={Style.name}>
          <span className={Style.firstName}>{info.firstName}</span>
          <br />
          <span className={Style.lastName}>{info.lastName}</span>
        </h1>

        {/* Typewriter */}
        <Typewriter texts={typewriterTexts} />

        {/* Hairline divider */}
        <div className={Style.hairline} aria-hidden="true" />

        {/* Technical spec tags */}
        <div className={Style.specRow}>
          {specs.map(s => <SpecTag key={s} label={s} />)}
        </div>

        {/* Mini bio */}
        <Box component="ul" className={Style.miniBio}>
          {info.miniBio.map((bio, i) => (
            <EmojiBullet key={i} emoji={bio.emoji} text={bio.text} />
          ))}
        </Box>

        {/* Social icons */}
        <Box
          display="flex"
          gap="0.75rem"
          justifyContent="center"
          className={Style.socials}
          flexWrap="wrap"
        >
          {info.socials.map((social, i) => (
            <SocialIcon key={i} link={social.link} icon={social.icon} label={social.label} />
          ))}
        </Box>

        {/* CTA */}
        <Box mt="1.75rem" display="flex" justifyContent="center">
          <DownloadButton />
        </Box>
      </div>
    </Box>
  );
}
